import { useCallback } from 'react';
import Link from 'next/link';
import InfiniteScroll from 'react-infinite-scroller';
import { useCustomSnackBar } from 'src/client/hooks/useCustomSnackBar';
import { paths } from 'src/utils/paths';
import { useRecruitmentListContext } from 'src/client/contexts/recruitments/RecruitmentList';
import { RecruitmentSearchDocument, RecruitmentSearchQuery, RecruitmentSearchQueryVariables } from 'client/generated';
import { Loader } from 'client/components/common/Loader';
import { RecruitmentListItem } from 'src/client/components/recruitments/RecruitmentList/Item';
import { NotFound } from 'src/client/components/recruitments/RecruitmentList/NotFound';
import { canSearch } from 'src/utils/searchCondition';
import { useQuery } from '@apollo/client';

export const RecruitmentList = () => {
  const { enqueueErrorSnackbar } = useCustomSnackBar();
  const { searchQueryParams } = useRecruitmentListContext();
  const { data, loading, fetchMore } = useQuery<RecruitmentSearchQuery, RecruitmentSearchQueryVariables>(
    RecruitmentSearchDocument,
    {
      variables: { data: { ...searchQueryParams } },
      skip: !canSearch({ prefectureIds: searchQueryParams.areaIds }),
      onError: (e) => {
        enqueueErrorSnackbar(e);
      },
    },
  );

  const cursor = data?.recruitmentSearch.paging.nextCursor;

  const recruitments = data?.recruitmentSearch.recruitments || [];

  const handleLoadMore = useCallback(async () => {
    if (loading || cursor == null) return;

    await fetchMore({
      variables: { data: { ...searchQueryParams, pagingCursor: cursor } },
    });
  }, [loading, cursor, fetchMore, searchQueryParams]);

  if (loading) {
    return <Loader />;
  }

  return (
    <ul>
      <InfiniteScroll
        pageStart={0}
        loadMore={handleLoadMore}
        hasMore={cursor != null}
        threshold={100}
        loader={<Loader key={0} />}
      >
        {recruitments.length == 0 && cursor == null ? (
          <NotFound />
        ) : (
          recruitments.map((o, index) => (
            <li key={index}>
              <Link
                href={`${paths.recruitments.index}/[id]`}
                as={`${paths.recruitments.index}/${encodeURIComponent(o.id)}`}
              >
                <a>
                  <RecruitmentListItem recruitment={o} />
                </a>
              </Link>
            </li>
          ))
        )}
      </InfiniteScroll>
      <style jsx>{`
        ul {
          list-style: none;
          margin: 0 20px 20px 20px;
          padding: 0;
        }
        li {
          display: inline-flex;
          width: 100%;
          justify-content: center;
        }
        a {
          margin-top: 30px;
          text-decoration: none;
        }
        .no-recruitings-found {
          margin-top: 50px;
          display: block;
        }
        .order-condition {
          margin-left: 23px;
        }
        .order-button {
          margin-right: 10px;
        }
        // TODO: PC版を実装するまでの一時的な対応 https://github.com/spotmate/baitry-worker-frontend-ssr/issues/119
        @media (min-width: 800px) {
          ul {
            width: 100%;
            max-width: 1022px;
            margin: 0 auto;
          }
        }
      `}</style>
    </ul>
  );
};
